import React from "react";

import { XCircle } from "lucide-react";

import {
  FLOW,
  askToMove,
  moveHereFrom,
  ownPublishedElsewhere,
} from "./flowCopy";
import { shortDate } from "./shared";
import type { UploadRejection } from "../../Types/AccountPlan";

interface Props {
  rejection: UploadRejection;
  filename: string;
  busy?: boolean;
  /** Why the last action on the card (move, dismiss) failed. */
  error?: string | null;
  /** Re-send the same file with `move_from` = the other account's copy. */
  onMove: (moveFrom: string) => void;
  onUploadDifferent: () => void;
  onDismiss: () => void;
  /** duplicate_here: open the copy already on this account (F-UF-F29). */
  onOpenExisting?: (fileId: string) => void;
}

const PRIMARY =
  "inline-flex h-8 items-center whitespace-nowrap rounded-lg bg-[#9333EA] px-3 text-xs font-semibold text-white hover:bg-[#7E22CE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:opacity-50";
const SECONDARY =
  "inline-flex h-8 items-center whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:opacity-50";

/** The card's heading, in plain words. */
export const rejectionTitle = (r: UploadRejection): string => {
  if (r.code === "duplicate_other")
    return r.other.account_name
      ? `This file is already on ${r.other.account_name}`
      : "This file is already another account's plan";
  if (r.code === "duplicate_here")
    // The plan number only (D1); an unconfirmed copy has none.
    return r.other.plan_version != null
      ? `Nothing new: this is the same file as v${r.other.plan_version}`
      : "Nothing new: this file is already uploaded here";
  return "This file can't be used";
};

/**
 * Upload flow Phase 3: an upload refused before it was read (exact duplicate,
 * wrong type, password, damaged, too many pages). It sits where the drop zone
 * was, not in a toast: nothing became a version, and the AE decides what next.
 * "Move it here" appears only when the server says the uploader may move the
 * other account's copy and names that copy.
 */
export const UploadRejectedCard: React.FC<Props> = ({
  rejection,
  filename,
  busy = false,
  error,
  onMove,
  onUploadDifferent,
  onDismiss,
  onOpenExisting,
}) => {
  const dup = rejection.code === "duplicate_other" ? rejection.other : null;
  // F-UF-F28: when the other account is visible, its facts line says it all; the
  // server's sentence repeated the same facts. Otherwise the sentence is all
  // there is (and it already says to ask the admin).
  const factsOnly = !!dup?.account_name;
  const existing =
    rejection.code === "duplicate_here"
      ? rejection.other.file_id || null
      : null;
  const moveFrom = dup?.movable ? dup.file_id || null : null;
  const facts =
    dup &&
    [
      dup.plan_version != null ? `v${dup.plan_version}` : null,
      dup.published ? "published" : "not published",
      dup.uploaded_by_name ? `uploaded by ${dup.uploaded_by_name}` : null,
      dup.uploaded_at ? shortDate(dup.uploaded_at) : null,
    ]
      .filter(Boolean)
      .join(" · ");
  return (
    <div
      role="alert"
      data-testid="upload-rejected"
      data-code={rejection.code}
      className="rounded-lg border border-red-200 bg-red-50 px-4 py-3"
    >
      <div className="flex items-start gap-2">
        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden />
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
              {FLOW.rejected}
            </span>
            <h3 className="text-sm font-semibold text-gray-900">
              {rejectionTitle(rejection)}
            </h3>
          </div>
          <p className="truncate text-xs text-gray-500" title={filename}>
            {filename}
          </p>
          {!factsOnly && (
            <p className="text-[13px] text-gray-800">{rejection.message}</p>
          )}
          {factsOnly && facts && (
            <p className="text-xs text-gray-600">
              {dup?.account_name ? `${dup.account_name}: ` : ""}
              {facts}
            </p>
          )}
          <p className="text-xs text-gray-500">{FLOW.rejectedBeforeReading}</p>
          {/* F-UF-E10: for an account the uploader can't open, the server's
              sentence ("…another account in your organization. Ask your
              admin.") is all there is; no "who to ask" line naming anyone. */}
          {dup && !moveFrom && !!dup.account_name && (
            <p className="text-xs text-gray-700">
              {dup.uploaded_by_you && dup.published
                ? ownPublishedElsewhere(dup.account_name)
                : askToMove(dup.uploaded_by_you ? null : dup.uploaded_by_name)}
            </p>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            {moveFrom && (
              <button
                type="button"
                disabled={busy}
                onClick={() => onMove(moveFrom)}
                className={PRIMARY}
              >
                {moveHereFrom(dup?.account_name ?? null)}
              </button>
            )}
            {existing && onOpenExisting && (
              <button
                type="button"
                disabled={busy}
                onClick={() => onOpenExisting(existing)}
                className={PRIMARY}
              >
                Open the existing copy
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={onUploadDifferent}
              className={moveFrom || existing ? SECONDARY : PRIMARY}
            >
              {FLOW.uploadDifferent}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onDismiss}
              className={SECONDARY}
            >
              {FLOW.dismiss}
            </button>
          </div>
          {error && (
            <p role="alert" className="text-xs text-red-700">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadRejectedCard;
